<script lang="ts">
export default {
  name: 'NotificationPage',
};
</script>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from 'vue';
import notificationService from '@/services/v2/NotificationService';
import { UserNotification } from '@/types/Notifications';
import { relativeDateTime } from '@/utilities/Dates';
import { MoreHorizontal, Dot } from 'lucide-vue-next';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { AppState, Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { sanitizeHtml } from '@/utilities/Sanitization';

// -----
// refs
// -----

const store = useStore<AppState>();
const router = useRouter();
const notificationOptions = ref<MenuItem[]>([]);

// ---------
// computed
// ---------

const notifications = computed(() => store.state.notifications);

// ----------------
// lifecycle hooks
// ----------------

onBeforeMount(async () => {
  notificationOptions.value = [
    {
      label: 'Delete Notification',
      handler: handleDeleteNotification,
    },
  ];
});

// ---------------------
// handlers / functions
// ---------------------

async function handleDeleteNotification(notification: UserNotification): Promise<void> {
  await notificationService.deleteNotification(notification.id);
  Commit(
    StoreMutationType.SET_NOTIFICATIONS,
    notifications.value.filter((n) => n.id !== notification.id),
  );
}

async function handleNotificationClick(notification: UserNotification): Promise<void> {
  await notificationService.markAsRead(notification.id);

  Commit(
    StoreMutationType.SET_NOTIFICATIONS,
    notifications.value.map((n) => {
      if (n.id === notification.id) {
        return { ...n, isRead: true };
      }
      return n;
    }),
  );

  switch (notification.type) {
    case 'new-policies-added':
      if (notification.payload && notification.payload.accountId) {
        router.push(`/account/${notification.payload.accountId}/policy-detail`);
      }
      break;
    case 'policies-up-for-renewal':
      if (notification.payload && notification.payload.accountId && notification.payload.programId) {
        router.push(`/account/${notification.payload.accountId}/programs/${notification.payload.programId}`);
      }
      break;
    case 'new-document-upload':
      if (notification.payload && notification.payload.accountId) {
        router.push(`/account/${notification.payload.accountId}/documents`);
      }
      break;
    default:
      break;
  }
}

function sanitizeNotificationMessage(message: string): string {
  return sanitizeHtml(message);
}
</script>

<template>
  <div class="tw:relative tw:flex tw:justify-center">
    <div class="layout tw:gap-4 tw:p-10">
      <div class="sidebar">
        <div class="tw:sticky tw:top-20">
          <section
            class="tw:flex tw:flex-col tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:p-4 tw:drop-shadow-xs">
            <h1 class="tw:text-lg">Manage your Notifications</h1>
            <router-link class="name" to="/notifications/preferences">
              <span class="tw:font-bold tw:underline">View Settings</span>
            </router-link>
          </section>
        </div>
      </div>
      <main class="main">
        <div class="tw:relative tw:rounded-xl tw:border tw:border-solid tw:border-gray-200 tw:drop-shadow-xs">
          <div v-if="notifications.length === 0" class="tw:min-h-full tw:min-w-full tw:p-10">
            <h3>No New Notifications</h3>
          </div>
          <section class="tw:flex tw:flex-col tw:items-center">
            <article
              v-for="notification of notifications"
              :key="notification.id"
              class="tw:w-full tw:cursor-pointer tw:border-x-0 tw:border-t-0 tw:border-b-1 tw:border-solid tw:border-gray-200 tw:p-4 tw:pl-1 tw:transition-colors tw:first:rounded-t-xl tw:last:rounded-b-xl tw:last:border-0"
              :class="{
                'tw:bg-base-100': notification.isRead,
                'tw:hover:bg-base-200': notification.isRead,
                'tw:border-gray-300 tw:bg-blue-100': !notification.isRead,
                'tw:hover:bg-blue-200/70': !notification.isRead,
              }"
              @click="handleNotificationClick(notification)">
              <div class="tw:flex tw:w-full tw:gap-2">
                <div :class="{ 'tw:invisible': notification.isRead }" class="tw:flex tw:self-center">
                  <Dot :size="40" class="tw:text-blue-700" />
                </div>
                <div class="tw:text-md tw:flex tw:w-full tw:max-w-md tw:flex-wrap tw:items-center tw:text-pretty">
                  <p v-html="sanitizeNotificationMessage(notification.message)"></p>
                </div>
                <div
                  class="tw:w-fit-content tw:flex tw:flex-col tw:items-end tw:justify-self-end tw:text-right tw:text-[9px]">
                  <p class="tw:text-end">{{ relativeDateTime(notification.createdAt) }}</p>
                  <MenuDropdown :items="notificationOptions">
                    <template #action>
                      <button @click.stop class="tw:btn tw:btn-circle tw:btn-ghost tw:btn-sm">
                        <MoreHorizontal class="tw:h-7 tw:w-7" />
                      </button>
                    </template>
                    <template #items="{ items, handleClick }">
                      <li v-for="item in items" :key="item.label">
                        <a
                          @click.stop="
                            () => {
                              handleClick();
                              item.handler(notification);
                            }
                          "
                          class="tw:rounded-lg tw:px-4 tw:py-2 tw:text-sm tw:text-gray-700 tw:no-underline">
                          {{ item.label }}
                        </a>
                      </li>
                    </template>
                  </MenuDropdown>
                </div>
              </div>
            </article>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.layout {
  display: grid;
  grid-template-columns: 225px 1fr;
  grid-template-areas: 'sidebar main';
}

.sidebar {
  grid-area: sidebar;
}

.main {
  grid-area: main;
}

.aside {
  grid-area: aside;
}
</style>
